import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateShipmentLineFormGroup } from './create-shipment-line-form-group.interface';

export interface CreateShipmentLineDefaults {
  salesOrderLineId: string;
  quantity: number;
  unitOfMeasureId: number | null;
  /**
   * Maximum allowed quantity for this line — the remaining (line.quantity - line.shippedQuantity).
   * Used to attach a Validators.max upper bound so over-ship is caught client-side too.
   * Ignored when `isServiceLine` is true (Service lines bypass the stock cap entirely on the
   * backend, so the client-side max would falsely block legitimate over-quantity entries).
   */
  maxQuantity: number;
  /**
   * True when the source inventory item is of type `Service`. Service lines drop the
   * `Validators.max` rule because the backend bypasses the stock cap server-side for
   * Service items. The controller-facing `/availability` endpoint returns the raw
   * `stock - reserved` projection (typically 0 for Service items); the `decimal.MaxValue`
   * sentinel is emitted only by the internal reservation path. Defaults to `false`.
   */
  isServiceLine?: boolean;
}

@Injectable()
export class CreateShipmentLineFormModel {
  private readonly formBuilder = inject(FormBuilder);

  public readonly fieldNames = {
    salesOrderLineId: 'salesOrderLineId',
    quantity: 'quantity',
    unitOfMeasureId: 'unitOfMeasureId',
  } as const;

  public buildFormGroup(defaults: CreateShipmentLineDefaults): FormGroup<CreateShipmentLineFormGroup> {
    return CreateShipmentLineFormModel.buildFormGroup(this.formBuilder, defaults);
  }

  public static buildFormGroup(
    formBuilder: FormBuilder,
    defaults: CreateShipmentLineDefaults,
  ): FormGroup<CreateShipmentLineFormGroup> {
    const quantityValidators = [
      Validators.required,
      Validators.min(0.0001),
      ...(defaults.isServiceLine ? [] : [Validators.max(defaults.maxQuantity)]),
    ];
    return formBuilder.group<CreateShipmentLineFormGroup>({
      salesOrderLineId: new FormControl<string | null>(defaults.salesOrderLineId, {
        validators: [Validators.required],
      }),
      quantity: new FormControl<number | null>(defaults.quantity, {
        validators: quantityValidators,
      }),
      unitOfMeasureId: new FormControl<number | null>(defaults.unitOfMeasureId, {
        validators: [Validators.required],
      }),
    });
  }
}
